import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X, Plus, Trash2, Check, Loader2, FileText, Calendar,
  ChevronDown, ChevronUp, Copy, Play, Users, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Lunedì', short: 'Lun' },
  { value: 1, label: 'Martedì', short: 'Mar' },
  { value: 2, label: 'Mercoledì', short: 'Mer' },
  { value: 3, label: 'Giovedì', short: 'Gio' },
  { value: 4, label: 'Venerdì', short: 'Ven' },
  { value: 5, label: 'Sabato', short: 'Sab' },
  { value: 6, label: 'Domenica', short: 'Dom' }
];

const SHIFT_TYPES = [
  { value: 'mattina', label: 'Mattina', time: '9:30-12:30' },
  { value: 'pomeriggio', label: 'Pomeriggio', time: '15:30-18:30' }
];

const ROOMS = ['Stanza A', 'Stanza B'];

export const TemplateModal = ({ templates, staff, onClose, onApply, onRefresh, getAuthHeaders }) => {
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', 'apply'
  const [saving, setSaving] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  
  // Form per nuovo template
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    assignments: []
  });
  
  // Form per applicare template
  const [applyForm, setApplyForm] = useState({
    templateId: '',
    startDate: '',
    weeksCount: 1 // Numero di settimane da generare (fino a 12 = 3 mesi)
  });

  // Filtra staff medici
  const doctors = staff.filter(s => s.role === 'doctor');
  const ortottisti = staff.filter(s => s.role === 'ortottista');

  const getRoleLabel = (role) => {
    if (role === 'doctor') return 'Oculista';
    if (role === 'ortottista') return 'Ortottista';
    return role;
  };

  const getStaffName = (staffId) => {
    const member = staff.find(s => s.id === staffId);
    return member ? `${member.first_name} ${member.last_name}` : 'N/A';
  };

  // Aggiungi assegnazione al template
  const addAssignment = () => {
    setNewTemplate({
      ...newTemplate,
      assignments: [
        ...newTemplate.assignments,
        {
          day_of_week: 0,
          shift_type: 'mattina',
          room: 'Stanza A',
          staff_id: '',
          paired_with_id: ''
        }
      ]
    });
  };

  // Rimuovi assegnazione
  const removeAssignment = (index) => {
    setNewTemplate({
      ...newTemplate,
      assignments: newTemplate.assignments.filter((_, i) => i !== index)
    });
  };

  // Aggiorna assegnazione
  const updateAssignment = (index, field, value) => {
    const updated = [...newTemplate.assignments];
    updated[index] = { ...updated[index], [field]: value };
    setNewTemplate({ ...newTemplate, assignments: updated });
  };

  // Crea template
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (newTemplate.assignments.length === 0) {
      toast.error('Aggiungi almeno un\'assegnazione');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/hr/shift-templates`, newTemplate, getAuthHeaders());
      toast.success('Template creato con successo!');
      setNewTemplate({ name: '', description: '', assignments: [] });
      setActiveTab('list');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  // Elimina template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await axios.delete(`${API}/hr/shift-templates/${templateId}`, getAuthHeaders());
      toast.success('Template eliminato');
      onRefresh();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Applica template a multiple settimane
  const handleApplyMultipleWeeks = async () => {
    if (!applyForm.templateId || !applyForm.startDate) {
      toast.error('Seleziona template e data di inizio');
      return;
    }
    
    setSaving(true);
    let totalCreated = 0;
    let totalErrors = [];
    
    try {
      const startDate = new Date(applyForm.startDate);
      
      for (let week = 0; week < applyForm.weeksCount; week++) {
        const weekStart = new Date(startDate);
        weekStart.setDate(startDate.getDate() + (week * 7));
        const dateStr = weekStart.toISOString().split('T')[0];
        
        try {
          const response = await axios.post(
            `${API}/hr/shift-templates/${applyForm.templateId}/apply?start_date=${dateStr}`,
            {},
            getAuthHeaders()
          );
          totalCreated += response.data.created || 0;
          if (response.data.errors?.length > 0) {
            totalErrors = [...totalErrors, ...response.data.errors];
          }
        } catch (err) {
          totalErrors.push(`Settimana ${week + 1}: ${err.response?.data?.detail || 'Errore'}`);
        }
      }
      
      toast.success(`${totalCreated} turni creati su ${applyForm.weeksCount} settimane!`);
      if (totalErrors.length > 0) {
        toast.warning(`${totalErrors.length} problemi riscontrati`);
      }
      
      setApplyForm({ templateId: '', startDate: '', weeksCount: 1 });
      setActiveTab('list');
      onClose();
    } catch (error) {
      toast.error('Errore nell\'applicazione del template');
    } finally {
      setSaving(false);
    }
  };

  // Duplica template esistente per modifica
  const duplicateTemplate = (template) => {
    setNewTemplate({
      name: `${template.name} (copia)`,
      description: template.description || '',
      assignments: [...(template.assignments || [])]
    });
    setActiveTab('create');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Template Turni</h3>
              <p className="text-sm text-gray-500">Pianifica fino a 3 mesi in anticipo</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="border-b px-6 py-2 flex gap-2 bg-gray-50">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'list' 
                ? 'bg-white shadow text-purple-600' 
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            📋 I Miei Template
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'create' 
                ? 'bg-white shadow text-purple-600' 
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            ➕ Crea Nuovo
          </button>
          <button
            onClick={() => setActiveTab('apply')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'apply' 
                ? 'bg-white shadow text-purple-600' 
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            🚀 Applica Template
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Tab: Lista Template */}
          {activeTab === 'list' && (
            <div className="space-y-4">
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-200 mb-4" />
                  <p className="text-gray-500 font-medium">Nessun template creato</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Crea un template per automatizzare la pianificazione dei turni
                  </p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4 inline mr-2" />
                    Crea il primo template
                  </button>
                </div>
              ) : (
                templates.map(template => (
                  <div key={template.id} className="bg-gray-50 rounded-xl overflow-hidden">
                    {/* Header Template */}
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                      onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Calendar className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{template.name}</p>
                          {template.description && (
                            <p className="text-sm text-gray-500">{template.description}</p>
                          )}
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                              {template.assignments?.length || 0} assegnazioni
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); duplicateTemplate(template); }}
                          className="p-2 text-gray-500 hover:bg-white rounded-lg"
                          title="Duplica"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {expandedTemplate === template.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Dettaglio Assegnazioni */}
                    {expandedTemplate === template.id && (
                      <div className="border-t bg-white p-4">
                        <div className="grid grid-cols-7 gap-2 text-center text-xs font-medium text-gray-500 mb-3">
                          {DAYS_OF_WEEK.map(d => (
                            <div key={d.value}>{d.short}</div>
                          ))}
                        </div>
                        
                        {/* Visualizzazione per giorno */}
                        <div className="grid grid-cols-7 gap-2">
                          {DAYS_OF_WEEK.map(day => {
                            const dayAssignments = template.assignments?.filter(a => a.day_of_week === day.value) || [];
                            return (
                              <div key={day.value} className="min-h-[80px] bg-gray-50 rounded-lg p-2 text-xs">
                                {dayAssignments.length === 0 ? (
                                  <span className="text-gray-300">-</span>
                                ) : (
                                  dayAssignments.map((a, idx) => (
                                    <div key={idx} className={`mb-1 p-1 rounded ${
                                      a.shift_type === 'mattina' ? 'bg-amber-100' : 'bg-blue-100'
                                    }`}>
                                      <div className="font-medium truncate">{getStaffName(a.staff_id)?.split(' ')[0]}</div>
                                      <div className="text-[10px] text-gray-500">{a.room?.replace('Stanza ', '')}</div>
                                    </div>
                                  ))
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => { 
                              setApplyForm({ ...applyForm, templateId: template.id }); 
                              setActiveTab('apply'); 
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <Play className="w-4 h-4" />
                            Applica Questo Template
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Tab: Crea Nuovo Template */}
          {activeTab === 'create' && (
            <form onSubmit={handleCreateTemplate} className="space-y-6">
              {/* Info Base */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Template *</label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Es: Settimana Standard, Team A..."
                    required
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                  <input
                    type="text"
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Turni Oculista + Ortottista standard"
                  />
                </div>
              </div>

              {/* Assegnazioni */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Assegnazioni Settimanali ({newTemplate.assignments.length})
                  </label>
                  <button
                    type="button"
                    onClick={addAssignment}
                    className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Aggiungi
                  </button>
                </div>

                {newTemplate.assignments.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Nessuna assegnazione</p>
                    <p className="text-sm text-gray-400">Clicca "Aggiungi" per creare la prima assegnazione</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {newTemplate.assignments.map((assignment, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-400">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {/* Giorno */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Giorno</label>
                            <select
                              value={assignment.day_of_week}
                              onChange={(e) => updateAssignment(index, 'day_of_week', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            >
                              {DAYS_OF_WEEK.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Turno */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Turno</label>
                            <select
                              value={assignment.shift_type}
                              onChange={(e) => updateAssignment(index, 'shift_type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            >
                              {SHIFT_TYPES.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Stanza */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Stanza</label>
                            <select
                              value={assignment.room}
                              onChange={(e) => updateAssignment(index, 'room', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            >
                              {ROOMS.map(r => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Staff Principale */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Staff *</label>
                            <select
                              value={assignment.staff_id}
                              onChange={(e) => updateAssignment(index, 'staff_id', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                              required
                            >
                              <option value="">Seleziona...</option>
                              <optgroup label="Oculisti">
                                {doctors.map(d => (
                                  <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                                ))}
                              </optgroup>
                              <optgroup label="Ortottisti">
                                {ortottisti.map(o => (
                                  <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>
                                ))}
                              </optgroup>
                            </select>
                          </div>
                          
                          {/* Binomio */}
                          <div className="relative">
                            <label className="block text-xs text-gray-500 mb-1">Binomio</label>
                            <div className="flex gap-1">
                              <select
                                value={assignment.paired_with_id}
                                onChange={(e) => updateAssignment(index, 'paired_with_id', e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                              >
                                <option value="">Nessuno</option>
                                <optgroup label="Oculisti">
                                  {doctors.filter(d => d.id !== assignment.staff_id).map(d => (
                                    <option key={d.id} value={d.id}>{d.first_name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Ortottisti">
                                  {ortottisti.filter(o => o.id !== assignment.staff_id).map(o => (
                                    <option key={o.id} value={o.id}>{o.first_name}</option>
                                  ))}
                                </optgroup>
                              </select>
                              <button
                                type="button"
                                onClick={() => removeAssignment(index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-700">
                  <strong>💡 Suggerimento:</strong> Crea assegnazioni per ogni combinazione di giorno/turno/stanza che usi regolarmente. 
                  Per esempio, se il Dr. Rossi lavora ogni Lunedì mattina in Stanza A con l'ortottista Bianchi, 
                  crea un'assegnazione per questa coppia.
                </p>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setNewTemplate({ name: '', description: '', assignments: [] }); setActiveTab('list'); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving || !newTemplate.name || newTemplate.assignments.length === 0}
                  className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  data-testid="save-template-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Salva Template
                </button>
              </div>
            </form>
          )}

          {/* Tab: Applica Template */}
          {activeTab === 'apply' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Applica Template a Multiple Settimane
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Seleziona Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template *</label>
                    <select
                      value={applyForm.templateId}
                      onChange={(e) => setApplyForm({ ...applyForm, templateId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                      data-testid="apply-template-select"
                    >
                      <option value="">Seleziona template...</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.assignments?.length || 0} assegnazioni)
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Data Inizio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Inizio Settimana *</label>
                    <input
                      type="date"
                      value={applyForm.startDate}
                      onChange={(e) => setApplyForm({ ...applyForm, startDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                      data-testid="apply-start-date"
                    />
                  </div>
                  
                  {/* Numero Settimane */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quante settimane? <span className="text-gray-400">(max 12 = 3 mesi)</span>
                    </label>
                    <select
                      value={applyForm.weeksCount}
                      onChange={(e) => setApplyForm({ ...applyForm, weeksCount: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                      data-testid="apply-weeks-count"
                    >
                      <option value={1}>1 settimana</option>
                      <option value={2}>2 settimane</option>
                      <option value={4}>4 settimane (1 mese)</option>
                      <option value={8}>8 settimane (2 mesi)</option>
                      <option value={12}>12 settimane (3 mesi - Trimestre)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Riepilogo */}
              {applyForm.templateId && applyForm.startDate && (
                <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                  <h5 className="font-medium text-gray-800 mb-3">📋 Riepilogo Operazione</h5>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Template: <strong>{templates.find(t => t.id === applyForm.templateId)?.name}</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-green-500" />
                      Dal: <strong>{new Date(applyForm.startDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-500" />
                      Per: <strong>{applyForm.weeksCount} settimane</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-green-500" />
                      Turni da creare: <strong>~{(templates.find(t => t.id === applyForm.templateId)?.assignments?.length || 0) * applyForm.weeksCount}</strong>
                    </li>
                  </ul>
                  
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-700">
                    ⚠️ I turni saranno creati in stato "pending". Lo staff dovrà confermarli.
                    Turni in conflitto con indisponibilità saranno saltati automaticamente.
                  </div>
                </div>
              )}

              {/* Pulsante Applica */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setActiveTab('list')}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  onClick={handleApplyMultipleWeeks}
                  disabled={saving || !applyForm.templateId || !applyForm.startDate}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                  data-testid="apply-template-button"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5" />
                  )}
                  Genera {applyForm.weeksCount} Settimane di Turni
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
